/**
 * Компонент аватарки авторизованного пользователя
 * 
 * Отображает круглую аватарку с инициалами пользователя и его ФИО.
 * При клике открывает выпадающее меню с опциями "Личный кабинет" и "Выйти".
 * Адаптивен: на мобильных устройствах скрывает ФИО, оставляя только аватарку.
 */

import React, { useState } from 'react';
import { Box, Avatar, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';

const UserAvatarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  padding: theme.spacing(0.5, 1),
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    opacity: 0.8,
  },
  // Адаптивность для мобильных устройств
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
    gap: 0,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1rem',
  fontWeight: 600,
  color: '#fff',
  // Адаптивность для мобильных устройств
  [theme.breakpoints.down('sm')]: {
    width: 36,
    height: 36,
    fontSize: '0.875rem',
  },
}));

const UserNameText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export interface UserAvatarProps {
  /** ФИО пользователя */
  userName: string;
  /** Номер телефона пользователя */
  userPhone: string;
  /** Callback при клике на "Личный кабинет" */
  onProfileClick?: () => void;
  /** Callback при клике на "Выйти" */
  onLogoutClick?: () => void;
}

/**
 * Извлекает инициалы из ФИО
 * 
 * @param name - ФИО пользователя
 * @returns Инициалы (первые буквы имени и фамилии)
 * 
 * @example
 * ```ts
 * getInitials('Иван Иванов') // 'ИИ'
 * getInitials('Петр') // 'П'
 * ```
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ').filter(Boolean);
  
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  
  // Берем первые буквы имени и фамилии
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/**
 * Компонент аватарки авторизованного пользователя
 * 
 * Отображает круглую аватарку с инициалами и ФИО пользователя.
 * При клике открывает выпадающее меню с опциями:
 * - Личный кабинет (переход на /lk)
 * - Выйти (выход из учетной записи)
 * 
 * На мобильных устройствах (< 960px) скрывает ФИО, показывая только аватарку.
 * 
 * @param userName - ФИО пользователя для отображения
 * @param userPhone - Номер телефона пользователя (для дополнительной информации)
 * @param onProfileClick - Опциональный callback при клике на "Личный кабинет" (по умолчанию переход на /lk)
 * @param onLogoutClick - Callback при клике на "Выйти"
 * 
 * @example
 * ```tsx
 * <UserAvatar 
 *   userName="Иван Иванов" 
 *   userPhone="+79001234567"
 *   onLogoutClick={() => handleLogout()}
 * />
 * ```
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  userName,
  userPhone,
  onProfileClick,
  onLogoutClick,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleProfileClick = () => {
    handleClose();
    if (onProfileClick) {
      onProfileClick();
    } else {
      // По умолчанию переходим на страницу ЛК
      navigate('/lk');
    }
  };
  
  const handleLogoutClick = () => {
    handleClose();
    if (onLogoutClick) {
      onLogoutClick();
    }
  };
  
  const initials = getInitials(userName);
  
  return (
    <>
      <UserAvatarContainer 
        onClick={handleClick} 
        role="button" 
        aria-label="Открыть меню пользователя"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <StyledAvatar alt={userName}>
          {initials}
        </StyledAvatar>
        <UserNameText>
          {userName}
        </UserNameText>
      </UserAvatarContainer>
      
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'user-menu-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: '8px',
          },
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Личный кабинет</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Выйти</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
